// referral-drawer.js - Referral drawer UI component

// Add validation error styles
if (!document.getElementById('referral-drawer-styles')) {
  const style = document.createElement('style');
  style.id = 'referral-drawer-styles';
  style.textContent = `
    #referral-form input.error {
      border-color: #dc2626 !important;
      background-color: #fef2f2 !important;
    }
    #referral-form input.error:focus {
      border-color: #dc2626 !important;
      outline-color: #dc2626 !important;
    }
    .referral-conversion-notice {
      background: rgba(210, 90, 40, 0.07);
      border-left: 3px solid #d25a28;
      border-radius: 0 0.375rem 0.375rem 0;
      padding: 0.625rem 0.875rem;
      margin-bottom: 1rem;
      font-size: 1rem;
      color: #5a3020;
      line-height: 1.45;
      font-style: italic;
    }
    #referral-error {
      background: #fef2f2;
      border-left: 3px solid #dc2626;
      border-radius: 0 0.375rem 0.375rem 0;
      padding: 0.625rem 0.875rem;
      margin-top: 1rem;
      font-size: 0.95rem;
      color: #991b1b;
      line-height: 1.45;
    }
  `;
  document.head.appendChild(style);
}

class ReferralDrawer extends DrawerComponent {
  constructor() {
    super();
  }

  get requiresBodyAppended() {
    return true;
  }

  get focusElement() {
    return this.querySelector('input[name="firstName"]');
  }

  connectedCallback() {
    super.connectedCallback();

    const form = this.querySelector('#referral-form');
    const successDiv = this.querySelector('#discount-success');
    const copyBtn = this.querySelector('#copy-discount-code');
    const continueBtn = this.querySelector('#continue-shopping');
    const title = this.querySelector('#referral-title');
    const subtitle = this.querySelector('#referral-subtitle');

    // Add validation styling
    if (form) {
      const inputs = form.querySelectorAll('input[required]');
      inputs.forEach(input => {
        input.addEventListener('blur', () => {
          const value = input.value.trim();
          if (value === '') {
            input.classList.add('error');
          } else if (input.hasAttribute('minlength') && value.length < parseInt(input.getAttribute('minlength'))) {
            input.classList.add('error');
          } else if (input.type === 'email' && !input.validity.valid) {
            input.classList.add('error');
          } else {
            input.classList.remove('error');
          }
        });

        input.addEventListener('input', () => {
          const value = input.value.trim();
          if (input.classList.contains('error')) {
            if (value !== '' && (!input.hasAttribute('minlength') || value.length >= parseInt(input.getAttribute('minlength')))) {
              if (input.type !== 'email' || input.validity.valid) {
                input.classList.remove('error');
              }
            }
          }
        });
      });

      form.addEventListener('submit', async (e) => {
        e.preventDefault();

        // Validate all fields
        let isValid = true;
        inputs.forEach(input => {
          if (input.value.trim() === '' || !input.validity.valid) {
            input.classList.add('error');
            isValid = false;
          }
        });

        if (!isValid) {
          form.reportValidity();
          return;
        }

        const discountCode = form.dataset.discountCode;
        const discountUrl = form.dataset.discountUrl;
        const discountData = JSON.parse(form.dataset.discountData || '{}');

        // Free-box eligibility gate: the free first box is for NEW customers
        // only, so block an email that already has a Wildgrain order. Scoped to
        // FO- (friend-offer) codes — referral-credit codes skip this. The
        // friend's email isn't known until this submit, so the check lives here.
        const errorEl = this.querySelector('#referral-error');
        const submitBtn = form.querySelector('button[type="submit"]');
        if (discountCode && discountCode.indexOf('FO-') === 0) {
          const submitEmail = form.querySelector('input[name="email"]').value.trim();
          if (errorEl) errorEl.classList.add('hidden');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.dataset.label = submitBtn.dataset.label || submitBtn.textContent;
            submitBtn.textContent = 'Checking…';
          }
          let ineligible = false;
          try {
            const eligRes = await fetch('https://api.wildgrain.com/api/friend-offer/check-eligibility', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ email: submitEmail })
            });
            // 403 = this email already has an order. Other failures (500/network)
            // fail open, matching the backend's allow-through behaviour, so a
            // transient error never blocks a legitimate new customer.
            ineligible = eligRes.status === 403;
          } catch (eligErr) {
            console.warn('[ReferralDrawer] Eligibility check failed, allowing through', eligErr);
          }
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.textContent = submitBtn.dataset.label || 'Claim Discount';
          }
          if (ineligible) {
            if (errorEl) {
              errorEl.textContent = 'This email already has a Wildgrain order, so it isn’t eligible for the free box — this offer is for new customers only.';
              errorEl.classList.remove('hidden');
            }
            form.querySelector('input[name="email"]').classList.add('error');
            return;
          }
        }

        // Set discount cookies
        if (discountData && discountData.code) {
          try {
            const sevenDays = 7;
            function setCookie(name, value, days) {
              const expires = new Date();
              expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
              document.cookie = name + '=' + encodeURIComponent(value) + '; expires=' + expires.toUTCString() + '; path=/';
            }

            setCookie('wg_discount_code', discountData.code, sevenDays);
            if (discountData.amount !== null) setCookie('wg_discount_amount', discountData.amount, sevenDays);
            if (discountData.qty !== null) setCookie('wg_discount_qty', discountData.qty, sevenDays);

            console.log('[ReferralDrawer] Discount cookies set:', discountData);
          } catch (cookieErr) {
            console.warn('Failed to set discount cookies', cookieErr);
          }
        }

        // Trim all input values
        inputs.forEach(input => {
          input.value = input.value.trim();
        });

        // Notify backend
        try {
          const email = form.querySelector('input[name="email"]').value;
          const firstName = form.querySelector('input[name="firstName"]').value;
          const lastName = form.querySelector('input[name="lastName"]').value;

          fetch('https://api.wildgrain.com/api/referrals/invite', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email: email,
              first_name: firstName,
              last_name: lastName,
              discount_code: discountCode
            })
          }).catch(err => console.warn('Failed to notify invite usage', err));
        } catch (inviteErr) {
          console.warn('Invite POST exception', inviteErr);
        }

        // Show success state
        const codeInput = this.querySelector('#discount-code-input');
        codeInput.value = discountCode;
        form.classList.add('hidden');
        title.classList.add('hidden');
        subtitle.classList.add('hidden');
        successDiv.classList.remove('hidden');

        // Copy functionality
        if (copyBtn && !copyBtn.dataset.initialized) {
          copyBtn.dataset.initialized = 'true';
          copyBtn.addEventListener('click', (ev) => {
            ev.preventDefault();
            if (navigator.clipboard && navigator.clipboard.writeText) {
              navigator.clipboard.writeText(discountCode).then(() => {
                copyBtn.classList.add('opacity-50');
                setTimeout(() => copyBtn.classList.remove('opacity-50'), 800);
              }).catch(err => console.warn('Clipboard copy failed', err));
            }
          });
        }

        // Continue shopping
        if (continueBtn && !continueBtn.dataset.initialized) {
          continueBtn.dataset.initialized = 'true';
          continueBtn.addEventListener('click', (e2) => {
            e2.preventDefault();
            window.location.replace(discountUrl);
          });
        }
      });
    }

    // Mark drawer as seen
    const expires = new Date();
    expires.setTime(expires.getTime() + 7 * 24 * 60 * 60 * 1000);
    document.cookie = 'wg_referral_drawer_seen=true; expires=' + expires.toUTCString() + '; path=/';
  }
}

customElements.define('referral-drawer', ReferralDrawer);

// Global function to show referral drawer
// options: { title, subtitle, conversionMessage }
window.showReferralDrawer = function(advocateName, discountCode, discountUrl, discountData, options) {
  var conversionMessage = options && options.conversionMessage || null;
  var title = options && options.title || (advocateName ? "You've been referred by " + advocateName + "!" : "You've been referred!");
  var subtitle = options && options.subtitle || 'Enter your details below to claim your exclusive discount.';
  console.log('[ReferralDrawer] showReferralDrawer called', { advocateName, discountCode, discountUrl, discountData });

  // Check if drawer already exists
  let drawer = document.getElementById('ReferralDrawer');

  if (!drawer) {
    // Create drawer HTML
    const drawerHTML = `
      <referral-drawer id="ReferralDrawer" class="referral-drawer drawer drawer--right" hidden>
        <div class="fixed-overlay" aria-controls="ReferralDrawer"></div>
        <div class="drawer__inner">
          <button class="drawer__close-btn z-1" aria-controls="ReferralDrawer" type="button" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="drawer__content flex flex-col h-full w-full">
            <div class="drawer__header">
              <h2 id="referral-title" class="drawer__heading h3 idler text-center" style="margin-top: 0!important;">
                ${title}
              </h2>
            </div>
            <div class="v-scrollable">
              <div class="drawer__body" style="padding-top: 0!important; padding-bottom: 1.5rem;">
                <p id="referral-subtitle" class="mb-s text-center w-full" style="margin-top: 0!important;">${subtitle}</p>
                ${conversionMessage ? `<div id="conversion-notice" class="referral-conversion-notice">${conversionMessage}</div>` : ''}
                <form id="referral-form" class="w-full" novalidate
                      data-discount-code="${discountCode}"
                      data-discount-url="${discountUrl}"
                      data-discount-data='${JSON.stringify(discountData)}'>
                  <div class="form-field mb-s">
                    <label class="form-label visually-hidden" for="referral-firstName">First Name</label>
                    <input placeholder="First Name" id="referral-firstName" name="firstName" required minlength="2" class="form-control w-full" type="text" autocomplete="given-name" />
                  </div>
                  <div class="form-field mb-s">
                    <label class="form-label visually-hidden" for="referral-lastName">Last Name</label>
                    <input placeholder="Last Name" id="referral-lastName" name="lastName" required minlength="2" class="form-control w-full" type="text" autocomplete="family-name" />
                  </div>
                  <div class="form-field mb-s">
                    <label class="form-label visually-hidden" for="referral-email">Email</label>
                    <input placeholder="Email" id="referral-email" name="email" required class="form-control w-full" type="email" autocomplete="email" />
                  </div>
                  <div class="form-field" style="margin-top: 1rem;">
                    <button type="submit" class="btn btn--primary w-full">Claim Discount</button>
                  </div>
                  <div id="referral-error" class="hidden" role="alert" aria-live="polite"></div>
                </form>
                <div id="discount-success" class="hidden">
                  <div style="margin-bottom: 1rem;">
                    <h2 id="discount-success-title" class="h2 mb-s idler text-center">Discount Applied!</h2>
                  </div>
                  <div class="form-field mb-s">
                    <div class="flex items-center" style="gap: 0.5rem;">
                      <input id="discount-code-input" readonly type="text" value="" class="form-control" style="flex: 1;" />
                      <button id="copy-discount-code" class="btn btn--secondary" aria-label="Copy discount code" type="button" style="flex-shrink: 0;">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M16 1H4C2.9 1 2 1.9 2 3v14c0 1.1 0.9 2 2 2h12c1.1 0 2-0.9 2-2V3c0-1.1-0.9-2-2-2zM4 17V3h12v14H4z" />
                          <path d="M20 5h-2v14H8v2c0 1.1 0.9 2 2 2h8c1.1 0 2-0.9 2-2V7c0-1.1-0.9-2-2-2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div style="margin-top: 1rem;">
                    <button id="continue-shopping" class="btn btn--primary w-full text-center" type="button">Continue Shopping</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </referral-drawer>
    `;

    document.body.insertAdjacentHTML('beforeend', drawerHTML);
    drawer = document.getElementById('ReferralDrawer');
  }

  // Show the drawer using the show() method from DrawerComponent
  drawer.show();
};
